import type { FooterConfig } from "./types";

/**
 * Official, fixed dispute-resolution links a RO e-commerce site is required to
 * surface. These are *official* destinations — not merchant-configurable — so
 * they live as constants in the widget rather than in per-site config.
 *
 * - SOL: the EU Online Dispute Resolution platform (Soluționarea Online a
 *   Litigiilor).
 * - SAL: ANPC's Alternative Dispute Resolution page (Soluționarea Alternativă a
 *   Litigiilor).
 */
const ANPC_SOL_URL = "https://ec.europa.eu/consumers/odr";
const ANPC_SAL_URL = "https://anpc.ro/ce-este-sal/";

/** Visitor-facing copy, seeded per banner language. */
const STRINGS = {
	ro: {
		heading: "Soluționarea litigiilor",
		solLabel: "ANPC SOL",
		solTitle: "Platforma SOL — Soluționarea Online a Litigiilor",
		salLabel: "ANPC SAL",
		salTitle: "ANPC — Soluționarea Alternativă a Litigiilor",
		paymentsHeading: "Plăți securizate",
	},
	en: {
		heading: "Dispute resolution",
		solLabel: "ANPC SOL",
		solTitle: "SOL — EU Online Dispute Resolution platform",
		salLabel: "ANPC SAL",
		salTitle: "ANPC — Alternative Dispute Resolution",
		paymentsHeading: "Secure payments",
	},
} as const;

/** Payment-trust marks, in display order, with their brand accent colors. */
const PAYMENT_MARKS = [
	{ key: "netopia", label: "NETOPIA", color: "#1b3b6f" },
	{ key: "visa", label: "VISA", color: "#1a1f71" },
	{ key: "mastercard", label: "Mastercard", color: "#eb001b" },
] as const;

type PaymentKey = (typeof PAYMENT_MARKS)[number]["key"];

/** True when the config asks for at least one badge to be shown. */
function hasAnyBadge(config: FooterConfig): boolean {
	if (config.anpcSol || config.anpcSal) return true;
	const p = config.payments;
	return Boolean(p && (p.netopia || p.visa || p.mastercard));
}

/**
 * Renders the footer trust kit: ANPC SOL/SAL badges (official links) plus any
 * enabled payment-trust marks. Mounted into a merchant-provided placeholder
 * (`[data-fewcookies-footer]`) when present, otherwise appended to the end of
 * `<body>` so it reads as a footer. Style-isolated via shadow DOM.
 */
export class Footer {
	private host: HTMLElement;
	private shadow: ShadowRoot;

	constructor(config: FooterConfig, language: "ro" | "en") {
		this.host = document.createElement("div");
		this.host.id = "fewcookies-footer";
		this.host.style.cssText = "display:block;width:100%;";
		// Open shadow root: same style isolation as the banner, but inspectable by
		// tests (the banner's closed root can't be asserted on).
		this.shadow = this.host.attachShadow({ mode: "open" });

		this.render(config, language);

		const target = document.querySelector("[data-fewcookies-footer]");
		if (target) {
			target.appendChild(this.host);
		} else {
			document.body.appendChild(this.host);
		}
	}

	destroy(): void {
		this.host.remove();
	}

	private render(config: FooterConfig, language: "ro" | "en"): void {
		const strings = STRINGS[language] ?? STRINGS.ro;

		const style = document.createElement("style");
		style.textContent = FOOTER_STYLES;

		const container = document.createElement("div");
		container.className = "fc-footer";

		// ── ANPC dispute-resolution badges (official links) ──
		const anpc = document.createElement("div");
		anpc.className = "fc-group";
		if (config.anpcSol) {
			anpc.appendChild(
				this.createLinkBadge(ANPC_SOL_URL, strings.solLabel, strings.solTitle, "fc-anpc"),
			);
		}
		if (config.anpcSal) {
			anpc.appendChild(
				this.createLinkBadge(ANPC_SAL_URL, strings.salLabel, strings.salTitle, "fc-anpc"),
			);
		}
		if (anpc.children.length > 0) container.appendChild(anpc);

		// ── Payment-trust marks (static, non-link) ──
		const payments = document.createElement("div");
		payments.className = "fc-group";
		for (const mark of PAYMENT_MARKS) {
			if (config.payments?.[mark.key as PaymentKey]) {
				payments.appendChild(this.createPaymentMark(mark.label, mark.color));
			}
		}
		if (payments.children.length > 0) container.appendChild(payments);

		this.shadow.append(style, container);
	}

	private createLinkBadge(
		href: string,
		label: string,
		title: string,
		className: string,
	): HTMLAnchorElement {
		const a = document.createElement("a");
		a.className = `fc-badge ${className}`;
		a.href = href;
		a.target = "_blank";
		a.rel = "noopener noreferrer nofollow";
		a.title = title;
		a.textContent = label;
		return a;
	}

	private createPaymentMark(label: string, color: string): HTMLElement {
		const span = document.createElement("span");
		span.className = "fc-badge fc-payment";
		span.style.color = color;
		span.textContent = label;
		return span;
	}
}

/**
 * Mount the footer trust kit if the config enables it and at least one badge is
 * selected. Returns the instance (so callers can destroy it) or null when
 * nothing was rendered.
 */
export function mountFooter(
	config: FooterConfig | undefined,
	language: "ro" | "en",
): Footer | null {
	if (!config?.enabled || !hasAnyBadge(config)) return null;
	return new Footer(config, language);
}

const FOOTER_STYLES = `
	:host {
		all: initial;
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
		font-size: 13px;
	}
	*, *::before, *::after { box-sizing: border-box; }
	.fc-footer {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: center;
		gap: 10px 18px;
		padding: 14px 16px;
	}
	.fc-group {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
	}
	.fc-badge {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 12px;
		border-radius: 6px;
		border: 1px solid rgba(0, 0, 0, 0.12);
		background: #fff;
		font-size: 12px;
		font-weight: 600;
		line-height: 1.2;
		letter-spacing: 0.02em;
		white-space: nowrap;
	}
	a.fc-badge {
		text-decoration: none;
		cursor: pointer;
		transition: border-color 0.15s ease, box-shadow 0.15s ease;
	}
	a.fc-badge:hover {
		border-color: rgba(0, 0, 0, 0.28);
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
	}
	.fc-anpc {
		color: #c0392b;
	}
	.fc-payment {
		text-transform: uppercase;
		letter-spacing: 0.06em;
	}
`;
